import {ArrowUpTrayIcon} from "@heroicons/react/20/solid";
import {ChangeEvent, DragEvent, KeyboardEvent, useRef, useState} from "react";

export type AttachmentDropzoneProps = {
  onFiles: (files: File[]) => void;
  accept?: string[];
  multiple?: boolean;
  disabled?: boolean;
  compact?: boolean;
};

/** Keyboard-accessible file picker with drag-and-drop support. */
export const AttachmentDropzone = ({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
  compact = false,
}: AttachmentDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const receiveFiles = (files: FileList | null) => {
    if (!disabled && files?.length) onFiles(Array.from(files));
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPicker();
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    receiveFiles(event.dataTransfer.files);
  };

  return (
    <>
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        tabIndex={-1}
        multiple={multiple}
        accept={accept?.join(",")}
        disabled={disabled}
        aria-label="Choose attachment files"
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          receiveFiles(event.target.files);
          event.target.value = "";
        }}/>
      <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label="Add attachment files"
      onClick={openPicker}
      onKeyDown={handleKeyDown}
      onDragEnter={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={[
        "group flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed text-center transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]",
        compact ? "gap-3 px-4 py-4" : "min-h-40 flex-col gap-3 px-6 py-8",
        dragging
          ? "border-[var(--ots-primary)] bg-[var(--ots-primary-container)]"
          : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] hover:border-[var(--ots-primary)] hover:bg-[var(--ots-background)]",
        disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "",
        ].join(" ")}>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--ots-primary-container)] text-[var(--ots-primary)]">
          <ArrowUpTrayIcon aria-hidden="true" className="size-5"/>
        </span>
        <span>
          <span className="block text-sm font-semibold text-[var(--ots-secondary)]">
            {compact ? "Add files" : "Drop files here or choose files"}
          </span>
          {!compact && (
            <span className="mt-1 block text-xs text-[var(--ots-text-muted)]">
              Add one or several files, then describe each before uploading.
            </span>
          )}
        </span>
      </div>
    </>
  );
};
